/**
 * The Report a problem / Suggest button's own small queue (FEEDBACK-PLAN.md §3.6).
 *
 * The device gives each report its id when the user presses Send, so sending
 * the same report again is always safe: the server answers a known id with the
 * reference it already saved. With no signal the report waits in this
 * browser's storage and goes when the device is back online, and again each
 * time the app opens. A report the server refuses (the user lost access, the
 * report is malformed) is dropped and the caller tells the user once — it never
 * retries forever.
 *
 * Separate from any app's own outbox (Measure2Quote's): a feedback report can
 * never hold up a job's sync, or the other way round.
 */

import type { FeedbackDiagnostics } from './feedback-diagnostics';

export type FeedbackKind = 'problem' | 'suggestion';

/** Pictures a report may carry (DD, 23 Oct). */
export const SCREENSHOTS_MAX = 5;
/**
 * The most one picture may be once shrunk on the device (the server checks it too). Five at
 * this size are 3.5 MB, 4.7 MB as base64: a whole report still fits the ~5 MB of browser
 * storage an iPad gives the offline queue.
 */
export const SCREENSHOT_MAX_BYTES = 700 * 1024;
/** The longest side a picture is shrunk to: an iPad screen is 2732 px; text stays readable at this size. */
export const SCREENSHOT_MAX_SIDE = 1600;

/** A picture the user attached, already shrunk to a JPEG on the device. */
export interface FeedbackScreenshot {
  fileName: string;
  /** The JPEG, base64 without the data: prefix. */
  base64: string;
}

/** What the device sends; the server adds who sent it (from the token) and the app's status. */
export interface FeedbackReport {
  id: string;
  kind: FeedbackKind;
  description: string;
  appBuild?: string;
  pagePath?: string;
  device?: string;
  jobRef?: string;
  /** ISO time the user pressed Send. */
  capturedOn: string;
  /** Up to SCREENSHOTS_MAX, in the order the user added them; sent with the report in one save. */
  screenshots?: FeedbackScreenshot[];
  /** Build, screen, device, connection and recent errors, for troubleshooting (#659, #660). */
  diagnostics?: FeedbackDiagnostics;
}

/** The report as kept for later; with no signal, its Diagnostics say it had to wait for one. */
function waitingCopy(report: FeedbackReport, noSignal: boolean): FeedbackReport {
  if (!noSignal || !report.diagnostics) return report;
  return { ...report, diagnostics: { ...report.diagnostics, connection: { ...report.diagnostics.connection, waitedOffline: true } } };
}

/** No signal, and the device has no room to keep this report (usually its picture) until there is. */
export class FeedbackQueueFullError extends Error {
  constructor() {
    super("No signal, and there isn't room on this device to keep this report with its pictures. Remove a picture and send again, or send when you're back online.");
    this.name = 'FeedbackQueueFullError';
  }
}

/**
 * Which account the report panel is working for. A draft and a submission belong to the
 * account that was signed in when they started: when another account signs in, the draft is
 * cleared and a submission still finishing is ignored, so one person's words and pictures
 * never show for, or report back to, the next person.
 */
export function createAccountGuard(initial: string | undefined) {
  let current = initial;
  return {
    /** Records the signed-in account; true when it changed (the draft must be cleared). */
    switchTo(accountId: string | undefined): boolean {
      const changed = accountId !== current;
      current = accountId;
      return changed;
    },
    /** True only while `accountId` (captured when the submission started) is still the signed-in one. */
    isCurrent(accountId: string | undefined): boolean {
      return accountId !== undefined && accountId === current;
    },
  };
}

/** The account this queue belongs to is no longer signed in. */
export class FeedbackQueueStoppedError extends Error {
  constructor() {
    super('You have been signed out. Sign in again to send this report.');
    this.name = 'FeedbackQueueStoppedError';
  }
}

/** Width and height that fit within `maxSide` on the longer side, keeping the shape; never enlarged. */
export function fitWithin(width: number, height: number, maxSide: number): { width: number; height: number } {
  const scale = Math.min(1, maxSide / Math.max(width, height));
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/** Bytes a base64 string decodes to. */
export function base64Bytes(base64: string): number {
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

type QueueStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export interface FeedbackQueueOptions {
  /** Sends one report; resolves to its reference (FB-01042). */
  send: (report: FeedbackReport) => Promise<string>;
  /** True when a failure is worth trying again later (no signal, server busy); false drops the report. */
  isRetryable: (err: unknown) => boolean;
  /**
   * True when a failure was no signal at all (offline, the network request failed), not a busy
   * server or an expired sign-in: only then does a kept report's Diagnostics say it waited
   * offline. Left out, no report is marked.
   */
  isNoSignal?: (err: unknown) => boolean;
  /** Where unsent reports wait; null (no storage available) keeps them in memory only. */
  storage: QueueStorage | null;
  /**
   * The signed-in account's Entra object id. Each account has its own queue, pictures
   * included: on a shared iPad, what one person saved offline is only ever sent when
   * that same person is signed in, never under the next person's name.
   */
  accountId: string;
  /**
   * The app the reports belong to ('measure-to-quote'). Each app has its own queue too: once
   * several apps show the button, a report saved in one is never sent through another's API
   * and filed under the wrong app.
   */
  appKey: string;
  key?: string;
}

export type SubmitOutcome = { status: 'sent'; reference: string } | { status: 'queued' };

export interface FlushOutcome {
  sent: { id: string; reference: string }[];
  dropped: { id: string; message: string }[];
}

const DEFAULT_KEY = 'higgins-feedback-queue';
/**
 * Queues were briefly keyed by account only (23 Oct 2026, the Measure2Quote test site), when
 * Measure2Quote was the only app with the button. Its queue adopts what was saved under that key.
 */
const ACCOUNT_ONLY_KEY_APP = 'measure-to-quote';

export function createFeedbackQueue(options: FeedbackQueueOptions) {
  if (!options.accountId) throw new Error('A feedback queue belongs to one signed-in account: accountId is required.');
  if (!options.appKey) throw new Error('A feedback queue belongs to one app: appKey is required.');
  const prefix = options.key ?? DEFAULT_KEY;
  const key = `${prefix}:${options.appKey}:${options.accountId}`;
  // This account's sender, fixed when the queue is made: a queue never sends with another account's.
  const send = options.send;
  let memory: FeedbackReport[] = [];
  let flushing: Promise<FlushOutcome> | null = null;
  // Set when this account is no longer the signed-in one: nothing more is sent, the reports stay kept.
  let stopped = false;

  function read(): FeedbackReport[] {
    if (!options.storage) return memory;
    try {
      const parsed: unknown = JSON.parse(options.storage.getItem(key) ?? '[]');
      return Array.isArray(parsed) ? (parsed as FeedbackReport[]) : [];
    } catch {
      return [];
    }
  }

  /** False when storage refused the write (full, or blocked); the in-memory copy still holds it. */
  function write(reports: FeedbackReport[]): boolean {
    memory = reports;
    try {
      options.storage?.setItem(key, JSON.stringify(reports));
      return true;
    } catch {
      return false;
    }
  }

  function add(report: FeedbackReport, noSignal: boolean): void {
    const before = read();
    // A report that can't be kept would be lost when the app closes: refuse it now, while the user is still there.
    if (!write([...before.filter((r) => r.id !== report.id), waitingCopy(report, noSignal)])) {
      write(before);
      throw new FeedbackQueueFullError();
    }
  }

  function remove(id: string): void {
    write(read().filter((r) => r.id !== id));
  }

  /** Measure2Quote's queue takes over this account's reports saved under the account-only key. */
  function adoptAccountOnlyQueue(): void {
    if (options.appKey !== ACCOUNT_ONLY_KEY_APP || !options.storage) return;
    const oldKey = `${prefix}:${options.accountId}`;
    let old: FeedbackReport[] = [];
    try {
      const parsed: unknown = JSON.parse(options.storage.getItem(oldKey) ?? '[]');
      old = Array.isArray(parsed) ? (parsed as FeedbackReport[]) : [];
    } catch {
      old = [];
    }
    if (old.length > 0) {
      const mine = read();
      const known = new Set(mine.map((r) => r.id));
      // Only drop the old key once its reports are safely under the new one.
      if (!write([...old.filter((r) => !known.has(r.id)), ...mine])) return;
    }
    try {
      options.storage.removeItem(oldKey);
    } catch {
      // Blocked storage: the old key stays; adopting it again later is harmless (by id).
    }
  }
  adoptAccountOnlyQueue();

  /**
   * Send now. No signal → kept for later ('queued'). Refused → thrown, nothing kept,
   * so the user sees the reason while still in the panel.
   */
  async function submit(report: FeedbackReport): Promise<SubmitOutcome> {
    if (stopped) throw new FeedbackQueueStoppedError();
    try {
      return { status: 'sent', reference: await send(report) };
    } catch (err) {
      if (!options.isRetryable(err)) throw err;
      add(report, options.isNoSignal?.(err) ?? false);
      return { status: 'queued' };
    }
  }

  async function sendWaiting(): Promise<FlushOutcome> {
    const outcome: FlushOutcome = { sent: [], dropped: [] };
    for (const report of read()) {
      // Checked before every send: an account that signed out mid-flush sends nothing more.
      if (stopped) break;
      try {
        const reference = await send(report);
        remove(report.id);
        outcome.sent.push({ id: report.id, reference });
      } catch (err) {
        if (options.isRetryable(err)) break; // still no signal: the rest wait too
        remove(report.id);
        outcome.dropped.push({ id: report.id, message: err instanceof Error ? err.message : String(err) });
      }
    }
    return outcome;
  }

  /** Send everything waiting, oldest first. One flush at a time. */
  function flush(): Promise<FlushOutcome> {
    flushing ??= sendWaiting().finally(() => {
      flushing = null;
    });
    return flushing;
  }

  /**
   * This account is no longer the signed-in one (signed out, or someone else signed in).
   * Nothing more is sent, even by a flush already running; its reports stay kept for it.
   */
  function stop(): void {
    stopped = true;
  }

  return { submit, flush, stop, waiting: () => read().length };
}

/** "iPad · Safari", "Phone · Chrome", "Desktop · Edge" — from the user agent, for triage. */
export function describeDevice(userAgent: string, maxTouchPoints = 0): string {
  // iPadOS asks for desktop sites and says "Macintosh"; only its touch screen gives it away.
  const isIpad = /iPad/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1);
  const kind = isIpad ? 'iPad' : /iPhone|Android.+Mobile|Mobile/.test(userAgent) ? 'Phone' : /Android/.test(userAgent) ? 'Tablet' : 'Desktop';
  const browser = /Edg/.test(userAgent)
    ? 'Edge'
    : /CriOS|Chrome/.test(userAgent)
      ? 'Chrome'
      : /FxiOS|Firefox/.test(userAgent)
        ? 'Firefox'
        : /Safari/.test(userAgent)
          ? 'Safari'
          : 'other browser';
  return `${kind} · ${browser}`;
}
